import { ChangeDetectionStrategy, Component, Input, OnDestroy, OnInit, signal, WritableSignal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { CardModule } from 'primeng/card';
import { TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { NotesService } from 'src/app/api/notes.service';
import { CustomerLogForStaff } from 'src/app/api/types/partner';
import { Categories, Note, NotePriorities, TripDetail } from 'src/app/api/commonInterfaces';
import { NoteCategoryPipe } from 'src/app/component-modules/notes/pipes/note-category.pipe';
import { NoteTypePipe } from 'src/app/component-modules/notes/pipes/note-type.pipe';
import { DatePipe } from '@angular/common';
import { LocalAuthService } from 'src/app/auth/local-auth.service';
import { RouterModule } from '@angular/router';
import { UiModule } from 'src/app/ui/ui.module';
import { of, Subscription } from 'rxjs';
import { Contact } from 'src/app/api/types/contacts';
import { EditScheduledActivityComponentInputs } from 'src/app/component-modules/notes/edit-scheduled-activity/edit-scheduled-activity.component';
import { DataPadService } from 'src/app/ui/data-pad/data-pad.service';
import { IconService } from 'src/app/ui/icon.service';
import { Column, TableColumnComponent, TableColumnsComponent, TableHeadersComponent } from 'src/app/ui/tables';
import { IconButtonComponent } from 'src/app/ui/icon-button/icon-button.component';
import { TableCaptionComponent } from 'src/app/ui/tables';
interface ScheduledActivityTableData extends CustomerLogForStaff {
 
  priorityString: string;
  phoneHref: string;
  canEdit: boolean;
  categoryString: string;
  contactName: string
}

@Component({
  selector: 'app-all-scheduled-activities',
  templateUrl: './all-scheduled-activities.component.html',
  styleUrls: ['./all-scheduled-activities.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    CardModule,
    TableModule,
    ButtonModule,
    TooltipModule,
    RouterModule,
    UiModule,
    TableColumnsComponent,
    TableHeadersComponent,
    IconButtonComponent, 
    TableCaptionComponent
  ],
  providers: [
    NoteCategoryPipe,
    NoteTypePipe,
    DatePipe
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AllScheduledActivitiesComponent implements OnInit, OnDestroy {
  activities = signal<ScheduledActivityTableData[]>([]);
  loading = signal(true);
  showAddActivityButton = false;

  @Input() staffId: number = 0;
  @Input() category: number | null = null;

  // Column definitions for the table
  columns: WritableSignal<Column<ScheduledActivityTableData>[]> = signal([
    { field: 'priorityString', header: 'Priority', type: 'chip', sortable: true, severityMap: { 'High': 'danger', 'Normal': 'info', 'Low': 'secondary' }, filterable: false },
    { field: 'dateScheduled', header: 'Scheduled', type: 'date', dateFormat: 'yyyy-MM-dd HH:mm', sortable: true, filterable: false },
    { field: 'groupName', header: 'Group', type: 'groupNameWithLink', custIdField: 'custId', sortable: true, filterable: false },
    { field: 'contactName', header: 'Contact', type: 'text', sortable: false, filterable: false },
    { field: 'logType', header: 'Type', type: 'logtype', sortable: true, filterable: false },
    { field: 'categoryString', header: 'Category', type: 'text', sortable: true, filterable: false },
    { field: 'notes', header: 'Note', type: 'html', sortable: false, filterable: false },
    { field: 'phone', header: 'Phone', type: 'link', linkField: 'phoneHref', sortable: false, filterable: false },
  ]);

  globalFilterFields: string[] = this.columns()
    .map(c => c.field)
    .filter(f => !!f);

  subscriptions: Subscription[] = [];

  constructor(
    private notesService: NotesService,
    private noteCategoryPipe: NoteCategoryPipe,
    private noteTypePipe: NoteTypePipe,
    private datePipe: DatePipe,
    private auth: LocalAuthService,
    private dataPadService: DataPadService,
    public icons: IconService
  ) { }

  ngOnInit() {
    this.showAddActivityButton = this.auth.currentUser?.newstaffId === this.staffId;
    this.loadActivities();

    this.subscriptions.push(this.notesService.scheduledActivitiesUpdated$.subscribe(() => this.loadActivities()));
  }

  ngOnDestroy() {
    this.subscriptions.forEach(subscription => subscription.unsubscribe());
  }

  private loadActivities() {
    if (!this.staffId) return;

    this.notesService.getAllScheduledActivitiesForStaff(this.staffId, this.category).subscribe({
      next: (activities) => {
        this.activities.set(this.transformActivities(activities));
        this.loading.set(false);
      },
      error: (error) => {
        console.error('Error loading activities:', error);
        this.loading.set(false);
      }
    });
  }

  private transformActivities(activities: CustomerLogForStaff[]): ScheduledActivityTableData[] {
    return activities.map(activity => ({
      ...activity,
      priorityString: this.getPriorityLabel(activity.priority),
      dateScheduled: activity.dateScheduled,
      groupName: activity.groupName,
      contactName: `${activity.contactFirstName ?? ''} ${activity.contactLastName ?? ''}`,
     
      categoryString: this.noteCategoryPipe.transform(activity.category),
      note: activity.notes ? activity.notes.replaceAll(/[\r\n\t]+/gm, ' ') : '',
      phone: activity.phone,
      phoneHref: `tel:${activity.phone?.replaceAll(/[^0-9]/g, '')}`,
      canEdit: this.notesService.canEditNote(this.auth.currentUser?.newstaffId, activity),
      custId: activity.custId
    }));
  }

  private getPriorityLabel(priority: number): string {
    switch (priority) {
      case NotePriorities.High:
        return 'High';
      case NotePriorities.Normal:
        return 'Normal';
      case NotePriorities.Low:
        return 'Low';
      default:
        return '';
    }
  }


  addActivity() {
    this.dataPadService.showPanel<EditScheduledActivityComponentInputs>(
      {
        panelName: "editScheduledActivity",
        inputData: {
          tripDetails: [],
          custId: 0,
          contacts: [],
          inputObservables: {
            contacts: of<Contact[]>([]),
            tripDetails: of<TripDetail[]>([])
          }
        }
      }
    )
  }

  editActivity(activity: ScheduledActivityTableData) {

    this.notesService.getCustomerDetailsForNoteEdit(activity.custId).subscribe({
      next: (results) => {
        this.dataPadService.showPanel<EditScheduledActivityComponentInputs>(
          {
            panelName: "editScheduledActivity",
            inputData: {
              tripDetails: results.tripDetails,
              custId: activity.custId,
              contacts: results.contacts,
              editActivity: activity
            }
          }
        )
      }
    })


  }

  completeActivity(activity: ScheduledActivityTableData) {
    this.notesService.getCustomerDetailsForNoteEdit(activity.custId).subscribe({
      next: (results) => {
        this.dataPadService.showPanel(
          {
            panelName: "completeScheduledActivity",
            inputData: {
              tripDetails: results.tripDetails,
              contacts: results.contacts,
              editActivity: activity,
              custId: activity.custId
            }
          }
        )
      }
    })
  }

  deleteActivityClick(activity: ScheduledActivityTableData) {
    this.dataPadService.showPanel({ panelName: "deleteNote", inputData: { note: activity }, outputs: { yesClick: (note: Note) => this.deleteActivity(note) } })
  }

  deleteActivity(note: Note) {
    this.notesService.deleteNote(note.id).subscribe({
      next: result => {
        this.activities.update(activities => activities.filter(a => a.id !== note.id))
      }
    })
  }

} 